import React, { useId } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { TouchableOpacity } from './Touchable';

const DESIGN_WIDTH = 181.025;
const DESIGN_HEIGHT = 34.273;
const DESIGN_RADIUS = 10;
const DESIGN_FONT = 13;
const DESIGN_LINE = 13.045;
const DESIGN_TRACKING = 0.0508;

const BASE_FILL = '#4E0100';
const BASE_BORDER = '#DA600E';
const BASE_BORDER_WIDTH = 0.2;
const GLOW_COLOR = '#FF7200';
const GLOW_OPACITY = 0.7;
const LABEL_COLOR = '#FFFF99';

const GREEN_BORDER = '#B8FF8C';
const GREEN_BORDER_WIDTH = 0.5;
const GREEN_STOPS = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
] as const;

export type CardActionButtonProps = {
	label: string;
	width: number;
	variant?: 'green' | 'dark';
	onPress?: () => void;
	disabled?: boolean;
};

export const CardActionButton = ({
	label,
	width,
	variant = 'green',
	onPress,
	disabled = false,
}: CardActionButtonProps) => {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');

	if (width <= 0) return null;

	const scale = width / DESIGN_WIDTH;
	const height = DESIGN_HEIGHT * scale;
	const radius = DESIGN_RADIUS * scale;
	const fillId = `cardAction${uid}`;

	return (
		<TouchableOpacity
			accessibilityRole='button'
			activeOpacity={0.85}
			disabled={disabled || !onPress}
			onPress={onPress}
			style={[
				styles.root,
				{
					width,
					height,
					borderRadius: radius,
					borderWidth: BASE_BORDER_WIDTH * scale,
					shadowRadius: 5.5 * scale,
					opacity: disabled ? 0.55 : 1,
				},
			]}
		>
			{variant === 'green' ? (
				<>
					<Svg width={width} height={height} style={StyleSheet.absoluteFill} pointerEvents='none'>
						<Defs>
							<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
								{GREEN_STOPS.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect
							x={0}
							y={0}
							width={width}
							height={height}
							rx={radius}
							ry={radius}
							fill={`url(#${fillId})`}
						/>
					</Svg>
					<View
						pointerEvents='none'
						style={[
							StyleSheet.absoluteFill,
							{
								borderRadius: radius,
								borderWidth: GREEN_BORDER_WIDTH * scale,
								borderColor: GREEN_BORDER,
							},
						]}
					/>
				</>
			) : null}

			<Text
				numberOfLines={1}
				style={[
					styles.label,
					{
						fontSize: DESIGN_FONT * scale,
						lineHeight: DESIGN_LINE * scale,
						letterSpacing: DESIGN_TRACKING * scale,
					},
				]}
			>
				{label}
			</Text>
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	root: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		backgroundColor: BASE_FILL,
		borderColor: BASE_BORDER,
		shadowColor: GLOW_COLOR,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: GLOW_OPACITY,
		elevation: 3,
	},
	label: {
		color: LABEL_COLOR,
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'center',
		includeFontPadding: false,
	},
});

export default CardActionButton;
